import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Input } from '@ophelos/ui/components/input';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { Calendar as CalendarIcon, ChevronDown, ChevronRight, CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { MultibancoReference } from '@/components/payments/MultibancoReference';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { PaymentPieChart } from '@/components/ui/PaymentPieChart';
import { STORAGE_KEYS } from '@ophelos/shared/constants/storage-keys';
import { currencySymbol } from '@/lib/currency';
import { useTranslation } from '@/hooks/useTranslation';
import { listPaymentSessionsFromConnect } from '@/lib/connect-api';
import { type MultibancoReference as MultibancoReferenceType } from '@/lib/payment-api';
import { useDebtData } from '@/contexts/DebtDataContext';
import { applyCredit } from '@/lib/accountNet';
import { discountedAmountMillicents } from '@/lib/discount-offers';

interface PartialPaymentTabProps {
  caseId: string;
  amount: number;
  /** Credit attributed to this debt; caps the partial at what paying in full would cost. */
  creditApplied?: number;
  currency?: string;
  onContinue?: () => void;
  allowDateSelect?: boolean; // If false, locks to "today"
  onSelectPaymentMethod?: (amount: number, type: 'full' | 'partial', date?: Date) => void;
  maxDaysInFuture?: number;
  minPartialPaymentAmount?: number;
  needsEmail?: boolean;
  payerEmail?: string;
  onPayerEmailChange?: (value: string) => void;
  emailValid?: boolean;
  getAccessToken?: () => Promise<string | null>;
  /** Operator deep-link prefill: suggested amount in subunits (cents). */
  initialAmountSubunits?: string | null;
}

export function PartialPaymentTab({
  caseId,
  amount,
  creditApplied = 0,
  currency = 'EUR',
  onContinue: _onContinue,
  allowDateSelect = false,
  onSelectPaymentMethod,
  maxDaysInFuture = 14,
  minPartialPaymentAmount = 0,
  needsEmail = false,
  payerEmail = '',
  onPayerEmailChange,
  emailValid = true,
  getAccessToken,
  initialAmountSubunits,
}: PartialPaymentTabProps) {
  const { t } = useTranslation();
  const { activeOffers } = useDebtData();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [open, setOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState(() => {
    const n = initialAmountSubunits ? Number.parseInt(initialAmountSubunits, 10) : Number.NaN;
    return Number.isFinite(n) && n > 0 ? (n / 100).toString() : '';
  });
  const [multibancoRef, setMultibancoRef] = useState<MultibancoReferenceType | null>(null);
  const [activePartialsSum, setActivePartialsSum] = useState(0);

  // Query active partial sessions to calculate maximum partial payment
  useEffect(() => {
    if (!caseId) {
      setActivePartialsSum(0);
      return;
    }

    async function fetchActivePartials() {
      try {
        const authStage = (localStorage.getItem(STORAGE_KEYS.AUTH_STAGE) as 'limited' | 'full' | null) ?? 'limited';
        const sessions = await listPaymentSessionsFromConnect(
          { caseId, statuses: ['pending', 'processing'], paymentType: 'partial' },
          authStage,
          getAccessToken,
        );

        // Sum amount_subunits from active sessions
        const sum = sessions.reduce((acc, session) => acc + (session.amount_subunits || 0), 0);

        setActivePartialsSum(sum);
      } catch (err) {
        console.error('[PartialPaymentTab] Unexpected error querying partials:', {
          case_id: caseId,
          error: err,
        });
        setActivePartialsSum(0); // Fail-open: no restriction
      }
    }

    fetchActivePartials();
  }, [caseId, getAccessToken]);

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + maxDaysInFuture);

  const isToday =
    selectedDate &&
    selectedDate.getDate() === today.getDate() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getFullYear() === today.getFullYear();

  // What clearing this debt costs right now — the figure the pay-in-full tab shows, derived
  // the same way: a live pay-now discount first, then the credit attributed to this debt.
  const offer = activeOffers[caseId];
  const discountedMillicents = offer ? discountedAmountMillicents(offer, 'pay_now', Math.round(amount * 100_000)) : null;
  const hasEligibleDiscount = offer != null && discountedMillicents != null && offer.status !== 'expired' && offer.status !== 'superseded';
  const discountedAmount = hasEligibleDiscount ? (discountedMillicents as number) / 100_000 : amount;
  // A part payment may not exceed that figure: above it the customer pays more for part of the
  // debt than clearing all of it costs — and with a Multibanco code, irreversibly. Paying the
  // whole figure here is allowed; the engine serves it as the pay-in-full product.
  const payableAmount = applyCredit(discountedAmount, creditApplied);
  // What is still owed after this payment settles, which is what the screen may claim. Credit
  // is ledger money and reduces it; the discount does not — a card or SEPA partial never
  // carries it (Payment.tsx discountApplies is full-payment only), so reading payableAmount
  // here would call a debt cleared with the discount's worth still owed.
  const owedAmount = applyCredit(amount, creditApplied);
  // Calculate max partial: payable figure - active partial sessions
  // Convert from subunits (cents) to units (euros)
  // Floors, matching the server's integer floor division: rounding here would show a
  // maximum the server computes as one subunit lower, and refuse the customer's own
  // quoted figure with exceeds_balance. Rounds to the nearest millicent first: payableAmount
  // is millicents divided down to euros, so `* 100` alone can land a hair under the true
  // integer (4.35 -> 434.99999999999994) and floor away a whole cent that was never owed.
  const amountSubunits = Math.floor(Math.round(payableAmount * 100_000) / 1_000);
  const maxPartialSubunits = Math.max(0, amountSubunits - activePartialsSum);
  const maxPartial = maxPartialSubunits / 100;

  const parsedAmount = parseFloat(customAmount) || 0;
  const isBelowMinimum = minPartialPaymentAmount > 0 && parsedAmount > 0 && parsedAmount < minPartialPaymentAmount;
  const lowestPayable = minPartialPaymentAmount > 0 ? minPartialPaymentAmount : 0.01;
  // Something is owed, but no amount fits under the ceiling: a credit or live discount narrowed
  // it below a minimum set against the gross balance, or a checkout in flight holds all of it.
  // Caught upfront rather than left for isBelowMinimum and the ceiling to take turns refusing
  // whatever the customer types.
  const noAmountFits = amountSubunits > 0 && maxPartial < lowestPayable;
  const nothingPayable = amountSubunits === 0;
  const isValidAmount = parsedAmount > 0 && parsedAmount <= maxPartial && !isBelowMinimum && !noAmountFits;

  const handleContinueToMethod = () => {
    if (!isValidAmount) {
      return;
    }
    if (onSelectPaymentMethod) {
      onSelectPaymentMethod(parsedAmount, 'partial', isToday ? undefined : selectedDate);
    }
  };

  // Amount Selection Step
  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-2 xs:px-4">
      <div className="flex-1 stack space-y-4 w-full">
        <div className="flex flex-col gap-4 max-w-lg w-full mx-auto">
          {/* Amount Display */}
          <div className="flex flex-col items-center gap-3 mt-14">
            <span className="font-semibold">{t('paymentTabs.payPartOfBalance')}</span>
            <div className="relative flex flex-row gap-2 items-center max-w-sm w-full">
              <div className="relative w-full">
                {/* Leading on purpose: this marks what the field accepts, so it stays
                    at the field's edge rather than following the locale's placement. */}
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-muted-foreground">{currencySymbol(currency)}</span>
                <Input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="0.00"
                  className="text-center text-5xl! font-bold h-18 pl-10 pr-4 rounded-3xl w-full shadow-xs"
                  min="0"
                  max={payableAmount}
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">
                {t('paymentTabs.totalBalance')}{' '}
                <CurrencyDisplay amount={owedAmount} currency={currency} className="text-sm text-foreground font-bold" />
              </span>
              {minPartialPaymentAmount > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground">
                    {t('paymentTabs.minimumPartialHint')}{' '}
                    <CurrencyDisplay amount={minPartialPaymentAmount} currency={currency} className="text-sm font-bold" />
                  </span>
                </div>
              )}
            </div>
          </div>

          {needsEmail && onPayerEmailChange && <PayerEmailField id="single-partial-payer-email" value={payerEmail} onChange={onPayerEmailChange} />}

          {/* Date Picker */}
          <div className="flex flex-row items-center justify-center gap-3 flex-wrap">
            {/* Date Picker (conditional based on allowDateSelect) */}
            {allowDateSelect ? (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="text-sm gap-0.5 bg-muted/60 border-border-heavy/50 shadow-none hover:bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-pointer!"
                  >
                    <CalendarIcon className="size-4 inline-block mr-2 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {isToday
                        ? t('paymentTabs.payToday')
                        : selectedDate
                          ? `${t('paymentTabs.payOn')} ${selectedDate.toLocaleDateString()}`
                          : t('paymentTabs.selectDate')}
                    </span>
                    <ChevronDown className="size-4 inline-block ml-2 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto overflow-hidden p-0 rounded-2xl" align="center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    captionLayout="dropdown-months"
                    startMonth={today}
                    endMonth={maxDate}
                    disabled={(date) => {
                      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
                      return dateOnly < todayOnly || dateOnly > maxDateOnly;
                    }}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-sm gap-0.5 bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-default">
                    <CalendarIcon className="size-4 inline-block mr-2 text-muted-foreground" />
                    <span className="text-sm font-medium">{t('paymentTabs.payToday')}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{t('paymentTabs.partialPaymentsMustBeToday')}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Pie Chart (shown when valid amount entered) */}
          {isValidAmount && (
            <div className="flex justify-center">
              <PaymentPieChart totalAmount={owedAmount} payingAmount={parsedAmount} currency={currency} />
            </div>
          )}

          {/* Validation Message */}
          {(noAmountFits || (customAmount && !isValidAmount)) && (
            <Alert variant="destructive">
              <CircleAlert />
              <AlertDescription>
                {/* One inline child: the description lays its children out as grid items, which would put each amount on its own row. */}
                <span>
                  {noAmountFits ? (
                    // A payment already in flight can be the whole reason nothing fits — "too
                    // small" is false in that case, and support can't act on it.
                    t(activePartialsSum > 0 ? 'payment.partialPaymentPending' : 'payment.minimumAbovePartialCeiling')
                  ) : nothingPayable ? (
                    // Any range would end at 0.00, below where it starts.
                    t('payment.nothingOutstanding')
                  ) : isBelowMinimum ? (
                    <>
                      {t('paymentTabs.minimumPartialAmount')}{' '}
                      <CurrencyDisplay amount={minPartialPaymentAmount} currency={currency} className="inline-flex gap-0 font-bold" />
                    </>
                  ) : (
                    <Trans
                      i18nKey="paymentTabs.enterValidAmount"
                      components={{
                        min: <CurrencyDisplay amount={lowestPayable} currency={currency} className="inline-flex gap-0 font-bold" />,
                        amount: <CurrencyDisplay amount={maxPartial} currency={currency} className="inline-flex gap-0 font-bold" />,
                      }}
                    />
                  )}
                </span>
              </AlertDescription>
            </Alert>
          )}

          {/* Warning about remaining balance — withheld only once nothing is owed after it */}
          {isValidAmount && parsedAmount < owedAmount && (
            <Alert variant="warning">
              <CircleAlert />
              <AlertDescription>{t('paymentTabs.willContactRemaining')}</AlertDescription>
            </Alert>
          )}
        </div>

        <AffordabilityCommitment />
      </div>

      {/* Multibanco Reference Modal */}
      <Dialog open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('payment.paymentReferenceTitle')}</DialogTitle>
          </DialogHeader>
          {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
        </DialogContent>
      </Dialog>

      <BottomActionBar>
        <Button onClick={handleContinueToMethod} size="xl" className="group w-full max-w-sm" disabled={!isValidAmount || !emailValid}>
          {isValidAmount ? (
            <>
              {t('paymentTabs.selectPayment')}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          ) : (
            t('paymentTabs.enterAmount')
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
